import { z } from "zod";

import { carrierPriceSchema } from "./carrier-schema.ts";
import { customerSchema } from "./customer-schema.ts";
import { idNameSchema } from "./generic-schema.ts";
import { addressSchema, locationSchema } from "./location-state-schema.ts";

// SubSchemas //
export const quoteAccessorialSchema = z.object({
	id: z.number(),
	description: z.string(),
});

export const quoteCommoditySchema = z.object({
	id: z.number().optional().nullable(),
	commodityId: z.number().optional().nullable(),
	description: z.string().nullable(),
	quantity: z.number(),
	unitTypeId: z.number(),
	freightClassId: z.number(),
	weight: z.number(),
	dimensionLength: z.number(),
	dimensionWidth: z.number(),
	dimensionHeight: z.number(),
	nmfc: z.string().nullable(),
	pieceCaseCount: z.number().nullable(),
	isHazmat: z.boolean(),
	isStackable: z.boolean(),
});

const detailedQuoteCommoditySchema = quoteCommoditySchema.extend({
	unitType: idNameSchema,
	freightClass: idNameSchema,
	unNumber: z.union([z.string(), z.number()]).nullable().optional(),
	packingGroupId: z.number().nullable().optional(),
	hazmatClassId: z.number().nullable().optional(),
	properShippingName: z.string().nullable().optional(),
	emergencyContactName: z.string().nullable().optional(),
	emergencyContactPhone: z.string().nullable().optional(),
});

const detailedUpdateCommoditySchema = z.object({
	commodityId: z.number(),
	description: z.string().nullable(),
	nmfc: z.string().nullable(),
	pieceCaseCount: z.number().nullable(),
	unNumber: z.union([z.string(), z.number()]).nullable().optional(),
	packingGroupId: z.number().nullable().optional(),
	hazmatClassId: z.number().nullable().optional(),
	properShippingName: z.string().nullable().optional(),
	emergencyContactName: z.string().nullable().optional(),
	emergencyContactPhone: z.string().nullable().optional(),
});

const accessorialSchema = z.object({
	id: z.number(),
	name: z.string(),
	accessorialUsageTypeId: z.number(),
	accessorialUsageType: idNameSchema,
	isActive: z.boolean(),
	stopTypeId: z.number().nullable(),
	stopType: idNameSchema.nullable(),
	ediCode: z.string().nullable(),
	p44APICode: z.string().nullable(),
	code: z.string().nullable(),
	isSubjectToFuelSurcharge: z.boolean(),
	isHazmat: z.boolean(),
});

const stopDetailsSchema = z
	.object({
		addressBookItemId: z.number().optional(),
		stopName: z.string().nullable(),
		contactName: z.string().nullable(),
		contactPhone: z.string().nullable(),
		contactExtension: z.string().nullable(),
		address1: z.string().nullable(),
		address2: z.string().nullable(),
		hoursOpen: z.string().nullable(),
		hoursClose: z.string().nullable(),
		isLegacy: z.boolean().optional(),
		location: locationSchema,
		puNumber: z.string().nullable(),
		deliveryPO: z.string().nullable(),
	})
	// .partial() allows fields like puNumber/deliveryPO to be optionally absent
	.partial();

const carrierRateExceptionsSchema = z.object({
	scac: z.string(),
	carrierName: z.string(),
	message: z.string(),
});

// GET Request Schemas //

export const quoteCustomerSchema = customerSchema.omit({
	hasSignedLTLAddendum: true,
	status: true,
});

// Stop details schema
const postStopDetailsSchema = z
	.object({
		addressBookItemId: z.number().optional(),
		stopName: z.string().nullable(),
		contactName: z.string().nullable(),
		contactPhone: z.string().nullable(),
		contactExtension: z.string().nullable(),
		address1: z.string().nullable(),
		address2: z.string().nullable(),
		hoursOpen: z.string().nullable(),
		hoursClose: z.string().nullable(),
		isLegacy: z.boolean().optional(),
		location: locationSchema,
		puNumber: z.string().nullable(),
		deliveryPO: z.string().nullable(),
	})
	.partial();

// Doubles as Schema for response on POST and PUT requests to quote API, when user adds insurance, or download rates
export const quoteSchema = z.object({
	id: z.number(),
	shipmentDate: z.string(),
	customerId: z.number(),
	customer: idNameSchema,
	origin: addressSchema,
	destination: addressSchema,
	quoteCommodities: z.array(detailedQuoteCommoditySchema),
	accessorials: z.array(accessorialSchema),
	carrierPrices: z.array(carrierPriceSchema),
	carrierRateExceptions: z
		.array(carrierRateExceptionsSchema)
		.nullable()
		.optional(), // TODO: Need to know what the data structure is
	quotePreviousRate: z.any().nullable(),
	pieceCaseCount: z.number().nullable(),
	distance: z.number().nullable(),
	selectedCarrierPriceId: z.number().nullable(),
	insuranceApplyTypeId: z.number().nullable(),
	insuranceProductTypeId: z.number().nullable(),
	insuranceValue: z.number().nullable(),
	statusLevel: z.number(),
	isCustomersFirstQuote: z.boolean(),
	createdDate: z.string(),
	customerReferences: z.string().nullable(),
	poNumbers: z.string().nullable(),
	soNumbers: z.string().nullable(),
	specialInstructions: z.string().nullable(),
	billingCustomerId: z.number().nullable(),
	pickupDetails: stopDetailsSchema,
	deliveryDetails: stopDetailsSchema,
	expirationDate: z.string(),
	totalLinearFeet: z.number().nullable(),
	calculatedLinearFeet: z.number().nullable(),
	isVolume: z.boolean(),
	isFinalMile: z.boolean(),
	publicGuid: z.string(),
	eTag: z.string(),
	createdByEmail: z.string(),
	poNumber: z.string().nullable(),
	createdByDistributorId: z.number().nullable(),
	isHiddenFromDistributor: z.boolean(),
});

// POST Request Schemas //

export const quoteBodySchema = z.object({
	origin: locationSchema,
	destination: locationSchema,
	isGuest: z.boolean(),
	accessorials: z.array(quoteAccessorialSchema),
	pieceCaseCount: z.number().nullable(),
	carrierPrices: z.array(z.any()).nullable(), // TODO: carrierPrices will be another large schema, with sub schemas, import and use after creation.
	quotePreviousRate: z.any().nullable(), // TODO: Investigate what the expected shape of this property is
	selectedCarrierPriceId: z.number().nullable(),
	insuranceApplyType: z.any().nullable(),
	insuranceApplyTypeId: z.number().nullable(),
	insuranceProductType: z.any().nullable(), // TODO: Investigate what the expected shape of this property is
	insuranceProductTypeId: z.number().nullable(),
	insuranceValue: z.any().nullable(),
	poNumber: z.string().nullable(),
	poNumbers: z.string().nullable(),
	soNumbers: z.string().nullable(),
	showBLNumber: z.boolean().nullable(),
	specialInstructions: z.string().nullable(),
	createdByDistributorId: z.number().nullable(),
	templateId: z.number().nullable(),
	customer: idNameSchema,
	id: z.number().nullable(),
	statusLevel: z.number().nullable(),
	customerReferences: z.string().nullable(),
	shipmentDate: z.string(),
	customerId: z.number(),
	quoteCommodities: z.array(quoteCommoditySchema),
	totalLinearFeet: z.string(),
	sourceId: z.number().nullable(),
	publicGuid: z.string().nullable(),
	pickupDetails: stopDetailsSchema.nullable(),
	deliveryDetails: stopDetailsSchema.nullable(),
});

export const quoteTemplateBodySchema = quoteBodySchema.extend({
	calculatedLinearFeet: z.number().nullable().optional(),
});

// POST Request Schemas //

// TODO: UI should be updated for quoteState to use the naming convention from this, so that we do not need two versions
// Quote commodity schema
const postQuoteCommoditySchema = z.object({
	id: z.number(),
	description: z.string().nullable(),
	unitTypeId: z.number(),
	unitType: idNameSchema,
	quantity: z.number(),
	weight: z.number(),
	dimensionLength: z.number(),
	dimensionWidth: z.number(),
	dimensionHeight: z.number(),
	freightClassId: z.number(),
	freightClass: idNameSchema,
	nmfc: z.string().nullable(),
	isStackable: z.boolean(),
	isHazmat: z.boolean(),
	pieceCaseCount: z.number().nullable(),
	unNumber: z.union([z.string(), z.number()]).nullable().optional(),
	packingGroupId: z.number().nullable().optional(),
	hazmatClassId: z.number().nullable().optional(),
	properShippingName: z.string().nullable().optional(),
	emergencyContactName: z.string().nullable().optional(),
	emergencyContactPhone: z.string().nullable().optional(),
	hazmatDetail: z
		.object({
			unNumber: z.union([z.string(), z.number()]).nullable().optional(),
			packingGroupId: z.number().nullable().optional(),
			hazmatClass: z.number().nullable().optional(),
			properShippingName: z.string().nullable().optional(),
			emergencyContactName: z.string().nullable().optional(),
			emergencyContactPhone: z.string().nullable().optional(),
		})
		.nullable()
		.optional(),
});

// Accessorial schema
const postAccessorialSchema = z.object({
	id: z.number(),
	name: z.string(),
	accessorialUsageTypeId: z.number(),
	accessorialUsageType: idNameSchema,
	isActive: z.boolean(),
	stopTypeId: z.number().nullable(),
	stopType: idNameSchema.nullable(),
	ediCode: z.string().nullable(),
	p44APICode: z.string().nullable(),
	code: z.string().nullable(),
	isSubjectToFuelSurcharge: z.boolean(),
	isHazmat: z.boolean(),
});

export const quoteResponseSchema = z.object({
	id: z.number(),
	shipmentDate: z.string(),
	customerId: z.number(),
	customer: idNameSchema,
	origin: locationSchema,
	destination: locationSchema,
	quoteCommodities: z.array(postQuoteCommoditySchema),
	accessorials: z.array(postAccessorialSchema),
	carrierPrices: z.array(z.any()),
	carrierRateExceptions: z.array(z.any()).nullable().optional(), // optional b/c customers cant have this
	quotePreviousRate: z.any().nullable(),
	pieceCaseCount: z.number().nullable(),
	distance: z.number().nullable(),
	selectedCarrierPriceId: z.number().nullable(),
	insuranceApplyTypeId: z.number().nullable(),
	insuranceProductTypeId: z.number().nullable(),
	insuranceValue: z.number().nullable(),
	statusLevel: z.number(),
	isCustomersFirstQuote: z.boolean(),
	createdDate: z.string(),
	customerReferences: z.string().nullable(),
	poNumbers: z.string().nullable(),
	soNumbers: z.string().nullable(),
	specialInstructions: z.string().nullable(),
	billingCustomerId: z.number().nullable(),
	pickupDetails: postStopDetailsSchema,
	deliveryDetails: postStopDetailsSchema,
	expirationDate: z.string(),
	totalLinearFeet: z.number().nullable(),
	calculatedLinearFeet: z.number().nullable(),
	isVolume: z.boolean(),
	isFinalMile: z.boolean(),
	publicGuid: z.string(),
	eTag: z.string(),
	createdByEmail: z.string().nullable(),
	poNumber: z.number().nullable(),
	createdByDistributorId: z.number().nullable(),
	isHiddenFromDistributor: z.boolean(),
	createdByUserId: z.number().optional().nullable(),
	hasRoutingGuideMatch: z.boolean().optional(),
});

// Endpoint: api/shipment-details/quotes/{:quoteId}/book-tender-shipment
// Tendering a rerate of a dispatched shipment cancels the original dispatch and
// redispatches it. Each result is omitted when that step wasn't attempted.
export const bookTenderResponseSchema = quoteResponseSchema.extend({
	cancelDispatchSuccess: z.boolean().nullish(),
	redispatchSuccess: z.boolean().nullish(),
});

// PUT Request Schemas //

// Endpoint: /api/shipment-details/quotes/{:quoteId}/update-shipment-details
export const quoteShipmentBodySchema = z.object({
	id: z.number().optional(),
	quoteId: z.number(),
	eTag: z.string(),
	quoteCommodities: z.array(detailedUpdateCommoditySchema),
	// Guards the Save path: a cleared Ship Date must not reach the API as an
	// unparseable string, because ShipmentDate binds to a non-nullable DateTime.
	shipmentDate: z
		.string()
		.refine((value) => !Number.isNaN(new Date(value).getTime()), {
			message: "shipmentDate must be a valid date",
		}),
	customerReferences: z.string().nullable(),
	poNumbers: z.string().nullable(),
	soNumbers: z.string().nullable(),
	blNumber: z.string().nullable(),
	specialInstructions: z.string().nullable(),
	billingCustomerId: z.number().nullable(),
	pickupDetails: stopDetailsSchema.nullable(),
	deliveryDetails: stopDetailsSchema.nullable(),
	insuranceValue: z.number().nullable().optional(),
});

// PUT Request Schemas //

// Endpoint: api/shipment-details/quotes/{:quoteId}/tender-shipment
export const quoteTenderBodySchema = z.object({
	id: z.number(),
	shipmentDate: z.string(),
	customerId: z.number(),
	customer: z.object({
		id: z.number(),
		name: z.string(),
	}),
	origin: z.object({
		name: z.string().nullable(),
		address1: z.string().nullable(),
		address2: z.string().nullable(),
		postalCode: z.string(),
		city: z.string(),
		state: z.string(),
		country: z.string(),
	}),
	destination: z.object({
		name: z.string().nullable(),
		address1: z.string().nullable(),
		address2: z.string().nullable(),
		postalCode: z.string(),
		city: z.string(),
		state: z.string(),
		country: z.string(),
	}),
	quoteCommodities: z.array(
		z.object({
			id: z.number(),
			description: z.string().nullable(),
			unitTypeId: z.number(),
			unitType: z.object({
				id: z.number(),
				name: z.string(),
			}),
			quantity: z.number(),
			weight: z.number(),
			dimensionLength: z.number(),
			dimensionWidth: z.number(),
			dimensionHeight: z.number(),
			freightClassId: z.number(),
			freightClass: z.object({
				id: z.number(),
				name: z.string(),
			}),
			nmfc: z.string(),
			isStackable: z.boolean(),
			isHazmat: z.boolean(),
			pieceCaseCount: z.number().nullable(),
			unNumber: z.union([z.string(), z.number()]).nullable().optional(),
			packingGroupId: z.number().nullable().optional(),
			hazmatClassId: z.number().nullable().optional(),
			properShippingName: z.string().nullable().optional(),
			emergencyContactName: z.string().nullable().optional(),
			emergencyContactPhone: z.string().nullable().optional(),
		}),
	),
	accessorials: z.array(
		z.object({
			id: z.number(),
			name: z.string(),
			accessorialUsageTypeId: z.number(),
			accessorialUsageType: z.object({
				id: z.number(),
				name: z.string(),
			}),
			isActive: z.boolean(),
			stopTypeId: z.number(),
			stopType: z.object({
				id: z.number(),
				name: z.string(),
			}),
			ediCode: z.string().nullable(),
			p44APICode: z.string().nullable(),
			code: z.string().nullable(),
			isSubjectToFuelSurcharge: z.boolean(),
			isHazmat: z.boolean(),
		}),
	),
	carrierPrices: z.array(
		z.object({
			id: z.number(),
			quoteId: z.number(),
			carrierId: z.number(),
			carrier: z.object({
				id: z.number(),
				name: z.string(),
				displayName: z.string(),
				tollFreeNumber: z.string(),
				isPreferred: z.boolean(),
				isCarrierOfTheYear: z.boolean(),
				isEconomy: z.boolean(),
				isPartials: z.boolean(),
				scac: z.string(),
			}),
			carrierRate: z.number(),
			customerRate: z.number(),
			distributorRate: z.number().nullable(),
			brokerMargin: z.number(),
			insuranceMarkup: z.number().nullable(),
			carrierQuoteId: z.string().nullable(),
			carrierQuoteExpirationDate: z.string(),
			carrierPriceTypeId: z.number(),
			carrierPriceType: z.object({
				id: z.number(),
				name: z.string(),
			}),
			serviceLevelId: z.number(),
			serviceLevel: z.object({
				id: z.number(),
				name: z.string(),
			}),
			serviceAreaTypeId: z.number().nullable(),
			serviceType: z.string(),
			serviceLevelDescription: z.string(),
			destinationTerminalCode: z.string().nullable(),
			originTerminalCode: z.string().nullable(),
			transitDays: z.number().nullable(),
			maxLiabilityOverride: z.number().nullable(),
			maxLiabilityNew: z.number(),
			maxLiabilityUsed: z.number(),
			eTag: z.string(),
			carrierTotalAccessorialCharges: z.number().nullable(),
			carrierTotalAccessorialFuelSurcharges: z.number().nullable(),
			customerTotalAccessorialCharges: z.number().nullable(),
			customerTotalAccessorialFuelSurcharges: z.number().nullable(),
			generatedPartialsRate: z.number().nullable(),
			priceCharges: z.any().nullable(),
		}),
	),
	carrierRateExceptions: z
		.array(
			z.object({
				scac: z.string(),
				carrierName: z.string(),
				message: z.string(),
			}),
		)
		.nullable(),
	quotePreviousRate: z.any().nullable(),
	pieceCaseCount: z.number().nullable(),
	distance: z.number().nullable(),
	// Vendor tenders that route to an LTL Account Representative come back
	// without a booked carrier, so this can be null/absent.
	selectedCarrierPriceId: z.number().nullable().optional(),
	insuranceApplyTypeId: z.number().nullable(),
	insuranceProductTypeId: z.number().nullable(),
	insuranceValue: z.number().nullable(),
	statusLevel: z.number(),
	isCustomersFirstQuote: z.boolean(),
	createdDate: z.string(),
	customerReferences: z.string().nullable(),
	poNumbers: z.string().nullable(),
	soNumbers: z.string().nullable(),
	specialInstructions: z.string().nullable(),
	billingCustomerId: z.number(),
	pickupDetails: z.object({
		stopName: z.string().nullable(),
		contactName: z.string().nullable(),
		contactPhone: z.string().nullable(),
		contactExtension: z.string().nullable(),
		address1: z.string().nullable(),
		address2: z.string().nullable(),
		hoursOpen: z.string().nullable(),
		hoursClose: z.string().nullable(),
		location: z.object({
			postalCode: z.string(),
			city: z.string(),
			state: z.string(),
			country: z.string(),
		}),
		puNumber: z.string().nullable(),
	}),
	deliveryDetails: z.object({
		stopName: z.string().nullable(),
		contactName: z.string().nullable(),
		contactPhone: z.string().nullable(),
		contactExtension: z.string().nullable(),
		address1: z.string().nullable(),
		address2: z.string().nullable(),
		hoursOpen: z.string().nullable(),
		hoursClose: z.string().nullable(),
		location: z.object({
			postalCode: z.string(),
			city: z.string(),
			state: z.string(),
			country: z.string(),
		}),
		deliveryPO: z.string().nullable(),
	}),
	expirationDate: z.string(),
	totalLinearFeet: z.number().nullable(),
	calculatedLinearFeet: z.number().nullable(),
	isVolume: z.boolean(),
	isFinalMile: z.boolean(),
	publicGuid: z.string(),
	eTag: z.string(),
	createdByEmail: z.string(),
	// Absent when a vendor tender is routed to an LTL Account Representative
	// instead of being booked against a carrier.
	poNumber: z.number().nullable().optional(),
	createdByDistributorId: z.number().nullable(),
	isHiddenFromDistributor: z.boolean(),
	createdByUserId: z.number(),
});

// Exported Types //
export type QuoteBody = z.infer<typeof quoteBodySchema>;
export type QuoteTemplateBody = z.infer<typeof quoteTemplateBodySchema>;
export type QuoteResponse = z.infer<typeof quoteResponseSchema>;
export type BookTenderResponse = z.infer<typeof bookTenderResponseSchema>;
export type Location = z.infer<typeof locationSchema>;
export type QuoteCustomer = z.infer<typeof quoteCustomerSchema>;
export type Quote = z.infer<typeof quoteSchema>;
export type QuoteCommodity = z.infer<typeof quoteCommoditySchema>;
export type Accessorial = z.infer<typeof accessorialSchema>;
export type QuoteShipmentBody = z.infer<typeof quoteShipmentBodySchema>;
export type PostQuoteCommodity = z.infer<typeof postQuoteCommoditySchema>;
export type QuoteTenderBody = z.infer<typeof quoteTenderBodySchema>;
